import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { object, string, bool } from 'prop-types';
import { isNil, partial, toUpper, head, tail, path, pathOr, split } from 'ramda';
import Grid from '@material-ui/core/Grid';
import Icon from '@material-ui/core/Icon';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import { formatMessage, isNilOrEmpty } from '../../../utils/helpers';
import useStyles from './styles';
import generalMessages from '../../../messages';
import Image from '../../Image';
import Tooltip from '../../Tooltip';

const TextPicker = ({
  className,
  formik,
  icon,
  id,
  inputProps,
  intl,
  label,
  labelStyle,
  placeholder,
  variant,
  capitalize,
  iconHidden = false,
  emptyIconBoxNeeded = true,
  imageIcon,
  type,
  tooltip,
  helptext,
  maxLength,
  placement,
  emailField,
  required,
  validationParams = {},
  ...rest
}) => {
  const classes = useStyles();

  const getIntlMessage = partial(formatMessage, [intl]);

  const tooltipContent = tooltip || helptext;
  const tooltipType = tooltip ? 'info' : 'help';

  const isError = field => {
    const fieldValue = pathOr('', split('.', field), formik.values);
    const fieldTouched = pathOr(false, split('.', field), formik.touched);
    if (fieldTouched && (isNilOrEmpty(fieldValue) || path(split('.', field), formik.errors)))
      return path(split('.', field), formik.errors);
    return undefined;
  };

  return (
    <Grid
      container
      alignItems={isError(id) ? 'center' : 'flex-end'}
      className={className}
      justify="space-between"
      spacing={variant ? 0 : 2}
    >
      {emptyIconBoxNeeded &&
        (iconHidden ? (
          <Grid item>
            <Box className={`icon empty-icon ${classes.emptyIcon}`} />
          </Grid>
        ) : (
          <Grid item className={classes.align}>
            {variant !== 'outlined' && (
              <Icon component={icon} className="icon icon-field" color="secondary" fontSize="small" />
            )}
            {variant !== 'outlined' && imageIcon && (
              <Image
                width="20px"
                height="20px"
                name={imageIcon}
                className="icon icon-field"
                color="secondary"
                fontSize="small"
              />
            )}
          </Grid>
        ))}
      <Grid item container alignItems="center" xs>
        <TextField
          error={!isNil(isError(id))}
          fullWidth
          helperText={isError(id) && <FormattedMessage {...generalMessages[isError(id)]} values={validationParams} />}
          id={id}
          InputProps={{
            ...inputProps,
            ...(capitalize
              ? {
                  inputProps: {
                    ...inputProps,
                    style: { textTransform: 'uppercase' },
                  },
                }
              : {}),
            ...(tooltip || helptext
              ? {
                  endAdornment: <Tooltip content={tooltipContent} type={tooltipType} placement={placement} />,
                }
              : {}),
          }}
          InputLabelProps={{
            classes: { root: labelStyle },
          }}
          label={
            <>
              {getIntlMessage(label)}
              {required && <span className={classes.required} />}
            </>
          }
          onBlur={e => {
            e.target.value = capitalize
              ? toUpper(e.target.value)
              : `${!emailField ? toUpper(head(e.target.value)) : head(e.target.value)}${tail(e.target.value)}`;
            formik.handleBlur(e);
            formik.handleChange(e);
          }}
          onChange={formik.handleChange}
          placeholder={placeholder ? getIntlMessage(placeholder) : undefined}
          value={path(split('.', id), formik.values)}
          variant={variant}
          type={type}
          {...rest}
        />
      </Grid>
    </Grid>
  );
};

TextPicker.propTypes = {
  className: string,
  formik: object.isRequired,
  icon: object,
  id: string,
  inputProps: object,
  intl: object,
  label: object,
  labelStyle: string,
  placeholder: object,
  variant: string,
  capitalize: bool,
  iconHidden: bool,
  emptyIconBoxNeeded: bool,
  imageIcon: string,
  type: string,
  tooltip: string,
  helptext: string,
  maxLength: object,
  placement: string,
  emailField: bool,
  required: bool,
  validationParams: object,
};

export default injectIntl(TextPicker);
